import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { CheckCircle, Close as CloseIcon } from '@mui/icons-material';
import COLORS from '../../../../resources/style/Themes';

const MissingTranslationsModal = ({
  open,
  onClose,
  translations,
  isLoading,
  currentTranslatingLanguage,
  onAdd,
  currentItem,
}) => {
  const [addedTranslations, setAddedTranslations] = useState(new Set());
  const [editableTranslations, setEditableTranslations] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [submitting, setSubmitting] = useState({});

  const resetState = () => {
    setAddedTranslations(new Set());
    setEditableTranslations({});
    setIsEditing({});
    setSubmitting({});
  };

  const handleTranslationChange = (languageCode, field, newText) => {
    setEditableTranslations((prev) => ({
      ...prev,
      [languageCode]: {
        ...prev[languageCode],
        [field]: newText,
      },
    }));
  };

  const handleBlur = (translation, field) => {
    setIsEditing((prev) => ({
      ...prev,
      [translation.languageCode + field]: false,
    }));
  };

  const handleAdd = async (translation) => {
    if (translation.status === 'error') return;

    setSubmitting((prev) => ({ ...prev, [translation.languageCode]: true }));
    const editedValues = editableTranslations[translation.languageCode] || {};

    const inboxMessageToAdd = {
      message:
        editedValues.message !== undefined
          ? editedValues.message
          : translation.translatedMessage,
      name: currentItem.name,
      url: currentItem.url,
      language_code: translation.languageCode,
      header:
        editedValues.header !== undefined
          ? editedValues.header
          : translation.translatedHeader,
    };

    try {
      const success = await onAdd(inboxMessageToAdd);
      if (success) {
        setAddedTranslations(
          (prev) => new Set([...prev, translation.languageCode]),
        );
      }
    } finally {
      setSubmitting((prev) => ({ ...prev, [translation.languageCode]: false }));
    }
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExited: resetState,
      }}
    >
      <DialogTitle
        sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
      >
        Missing Translations for {currentItem?.header || ''}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CircularProgress sx={{ mt: 8 }} />
            <Typography sx={{ mt: 2 }}>
              {currentTranslatingLanguage
                ? `Translating to ${currentTranslatingLanguage}...`
                : 'Loading translations...'}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Language</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Translated Header</TableCell>
                <TableCell>Translated Message</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {translations.map((translation, index) => (
                <TableRow key={index}>
                  <TableCell>{translation.languageName}</TableCell>
                  <TableCell>{translation.languageCode}</TableCell>
                  <TableCell
                    onClick={() =>
                      translation.status !== 'error' &&
                      setIsEditing((prev) => ({
                        ...prev,
                        [translation.languageCode + 'header']: true,
                      }))
                    }
                    sx={{
                      cursor:
                        translation.status === 'error' ? 'default' : 'pointer',
                    }}
                  >
                    {translation.status === 'error' ? (
                      <Box sx={{ color: 'error.main' }}>
                        {translation.error}
                      </Box>
                    ) : isEditing[translation.languageCode + 'header'] ? (
                      <input
                        autoFocus
                        value={
                          editableTranslations[translation.languageCode]
                            ?.header !== undefined
                            ? editableTranslations[translation.languageCode]
                                .header
                            : translation.translatedHeader
                        }
                        onChange={(e) =>
                          handleTranslationChange(
                            translation.languageCode,
                            'header',
                            e.target.value,
                          )
                        }
                        onBlur={() => handleBlur(translation, 'header')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleBlur(translation, 'header');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    ) : editableTranslations[translation.languageCode]
                        ?.header !== undefined ? (
                      editableTranslations[translation.languageCode].header
                    ) : (
                      translation.translatedHeader
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      translation.status !== 'error' &&
                      setIsEditing((prev) => ({
                        ...prev,
                        [translation.languageCode + 'message']: true,
                      }))
                    }
                    sx={{
                      cursor:
                        translation.status === 'error' ? 'default' : 'pointer',
                    }}
                  >
                    {translation.status === 'error' ? (
                      <Box sx={{ color: 'error.main' }}>
                        {translation.error}
                      </Box>
                    ) : isEditing[translation.languageCode + 'message'] ? (
                      <input
                        autoFocus
                        value={
                          editableTranslations[translation.languageCode]
                            ?.message !== undefined
                            ? editableTranslations[translation.languageCode]
                                .message
                            : translation.translatedMessage
                        }
                        onChange={(e) =>
                          handleTranslationChange(
                            translation.languageCode,
                            'message',
                            e.target.value,
                          )
                        }
                        onBlur={() => handleBlur(translation, 'message')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleBlur(translation, 'message');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    ) : editableTranslations[translation.languageCode]
                        ?.message !== undefined ? (
                      editableTranslations[translation.languageCode].message
                    ) : (
                      translation.translatedMessage
                    )}
                  </TableCell>
                  <TableCell>
                    {translation.status === 'error' ? (
                      <Box sx={{ color: 'error.main' }}>Failed</Box>
                    ) : submitting[translation.languageCode] ? (
                      <CircularProgress size={24} />
                    ) : addedTranslations.has(translation.languageCode) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'success.main',
                        }}
                      >
                        <CheckCircle sx={{ mr: 1 }} />
                        Added
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleAdd(translation)}
                      >
                        Add
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingTranslationsModal;
